import { queryOptions, useQuery } from '@tanstack/react-query';
import { api, ApiError } from './api';
import type { Me } from './types';

export const meQuery = queryOptions({
  queryKey: ['me'],
  queryFn: async (): Promise<Me | null> => {
    try {
      return await api.get<Me>('/v1/auth/me');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return null;
      throw err;
    }
  },
  staleTime: 30_000,
});

/** The signed-in user; only call inside the authenticated layout. */
export function useMe(): Me {
  const { data } = useQuery(meQuery);
  if (!data) throw new Error('useMe outside an authenticated route');
  return data;
}

export function hasScope(me: Me, scope: string) {
  return me.scopes.includes(scope);
}

export const can = {
  read: (me: Me) => hasScope(me, 'register:read'),
  record: (me: Me) => hasScope(me, 'register:record'),
  admin: (me: Me) => hasScope(me, 'register:admin'),
  downloadDocuments: (me: Me) => hasScope(me, 'documents:read'),
  readAudit: (me: Me) => hasScope(me, 'audit:read'),
  readStats: (me: Me) => hasScope(me, 'stats:read'),
  /** Writes need a client: super admins must choose one first. */
  write: (me: Me) => me.user.role !== 'SUPER_ADMIN' || !!me.actingClient,
};
